import type { IntakeCondition, IntakeField, IntakeFormSchema, IntakeOption, IntakeRequester, IntakeValues } from "@/api/ticket-intake";

export type IntakeProjectResolution = { status: "matched"; projectId: string } | { status: "unavailable" | "ambiguous" };

/** A category can be committed only after its directory confirms it is a leaf. */
export function validateIntakeCategory(
  categoryId: string | null | undefined,
  categories: ReadonlyArray<{ id: string; parentId?: string | null }> | undefined,
): "required" | "categoryUnavailable" | "categoryLeafRequired" | null {
  if (!categoryId) return "required";
  if (!categories?.some((category) => category.id === categoryId)) return "categoryUnavailable";
  return categories.some((category) => category.parentId === categoryId) ? "categoryLeafRequired" : null;
}

/** Resolve only the host's exact code within the creator's authorized catalog. */
export function resolveIntakeProject(projects: ReadonlyArray<{ id: string; code: string }>, projectCode: string): IntakeProjectResolution {
  const code = projectCode.trim().toLowerCase();
  const matches = code ? projects.filter((project) => project.code.trim().toLowerCase() === code) : [];
  const match = matches[0];
  if (!match) return { status: "unavailable" };
  if (matches.length !== 1) return { status: "ambiguous" };
  return { status: "matched", projectId: match.id };
}

export type IntakeInputState = { pristine: string; synchronized: string };

function inputFingerprint(values: unknown): string {
  return JSON.stringify(values, (_key, value) =>
    value && typeof value === "object" && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value).sort(([left], [right]) => left.localeCompare(right)))
      : value,
  );
}

export function initializeIntakeInput(values: unknown): IntakeInputState {
  const fingerprint = inputFingerprint(values);
  return { pristine: fingerprint, synchronized: fingerprint };
}

/** An internal PATCH never marks a user's unsubmitted input as safe to leave. */
export function synchronizeIntakeInput(state: IntakeInputState, values: unknown): IntakeInputState {
  return { ...state, synchronized: inputFingerprint(values) };
}

export function intakeInputStatus(state: IntakeInputState | null, values: unknown): { dirty: boolean; needsSynchronization: boolean } {
  if (!state) return { dirty: false, needsSynchronization: false };
  const fingerprint = inputFingerprint(values);
  return { dirty: fingerprint !== state.pristine, needsSynchronization: fingerprint !== state.synchronized };
}

export function normalizeIntakeRequester(requester?: IntakeRequester | null): IntakeRequester {
  return Object.fromEntries((["name", "phone", "email"] as const).map((key) => [key, requester?.[key]?.trim() || null]));
}

/** Lookup labels/contact data aid input only; the server re-resolves customer identity at submit. */
export function requesterFromLookup(option: IntakeOption | null): IntakeRequester {
  return normalizeIntakeRequester(
    Object.fromEntries((["name", "phone", "email"] as const).map((key) => [key, typeof option?.data?.[key] === "string" ? option.data[key] : null])),
  );
}

/** Customer lookup data prefills the current order; submission still resolves trusted business facts on the server. */
export function selectIntakeCustomer(schema: IntakeFormSchema, context: IntakeValues, fieldValues: IntakeValues, option: IntakeOption | null) {
  const nextContext = { ...context };
  for (const key of ["customerId", "customerIdLabel", "customerLookup", "orderId", "orderIdLabel"]) delete nextContext[key];
  if (option) {
    nextContext.customerId = option.value;
    nextContext.customerIdLabel = option.label;
    const lookup = option.data?.lookup;
    if (lookup && typeof lookup === "object" && !Array.isArray(lookup)) nextContext.customerLookup = lookup;
    const orderId = typeof option.data?.orderId === "string" ? option.data.orderId.trim() : "";
    if (orderId) {
      nextContext.orderId = orderId;
      nextContext.orderIdLabel = typeof option.data?.orderIdLabel === "string" ? option.data.orderIdLabel.trim() : orderId;
    }
  }
  return {
    context: nextContext,
    requester: requesterFromLookup(option),
    fieldValues: clearContextDependencies(
      schema,
      fieldValues,
      ["customerId", "orderId"].filter((key) => context[key] !== nextContext[key]),
      nextContext,
    ),
  };
}

export function isFilled(value: unknown): boolean {
  return value !== undefined && value !== null && (typeof value !== "string" || value.trim() !== "") && (!Array.isArray(value) || value.length > 0);
}

export function matchesCondition(condition: IntakeCondition | null | undefined, values: IntakeValues): boolean {
  if (!condition) return true;
  if (condition.all) return condition.all.every((item) => matchesCondition(item, values));
  if (condition.any) return condition.any.some((item) => matchesCondition(item, values));
  const actual = values[condition.field ?? ""];
  if (condition.operator === "filled") return isFilled(actual);
  if (condition.operator === "in") return (condition.values ?? []).some((value) => JSON.stringify(value) === JSON.stringify(actual));
  return JSON.stringify(actual) === JSON.stringify(condition.value);
}

export function fieldVisible(field: IntakeField, values: IntakeValues): boolean {
  return matchesCondition(field.visibleWhen, values);
}

/** Clear transitively dependent fields and values hidden by a changed condition. */
export function changeIntakeValue(schema: IntakeFormSchema, values: IntakeValues, context: IntakeValues, key: string, value: unknown): IntakeValues {
  const next = { ...values, [key]: value };
  const changed = new Set([key]);
  for (let iteration = 0; iteration <= schema.fields.length; iteration++) {
    let propagated = false;
    for (const field of schema.fields) {
      if (field.key === key) continue;
      if ((field.dependsOn ?? []).some((dependency) => changed.has(dependency)) || !fieldVisible(field, { ...context, ...next })) {
        if (!changed.has(field.key)) {
          changed.add(field.key);
          propagated = true;
        }
        if (Object.hasOwn(next, field.key)) {
          delete next[field.key];
          propagated = true;
        }
      }
    }
    if (!propagated) break;
  }
  return next;
}

export function clearContextDependencies(schema: IntakeFormSchema, values: IntakeValues, changedContext: string[], context: IntakeValues = {}): IntakeValues {
  let next = { ...values };
  for (const key of changedContext) {
    next = changeIntakeValue(schema, next, context, key, context[key]);
    delete next[key];
  }
  return next;
}

export function migrateIntakeValues(previous: IntakeFormSchema, next: IntakeFormSchema, values: IntakeValues, context: IntakeValues): IntakeValues {
  const compatible = Object.fromEntries(
    next.fields
      .filter((field) => previous.fields.some((old) => old.key === field.key && old.type === field.type))
      .map((field) => [field.key, values[field.key]]),
  );
  return Object.fromEntries(
    next.fields
      .filter((field) => fieldVisible(field, { ...context, ...compatible }) && compatible[field.key] !== undefined)
      .map((field) => [field.key, compatible[field.key]]),
  );
}

export function validateIntakeValues(
  schema: IntakeFormSchema,
  values: IntakeValues,
  context: IntakeValues,
  messages: { required: string; number: string; range: string; length: string; scale: string },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const all = { ...context, ...values };
  for (const field of schema.fields) {
    if (!fieldVisible(field, all)) continue;
    const value = values[field.key];
    const required = field.required || (field.requiredWhen ? matchesCondition(field.requiredWhen, all) : false);
    if (!isFilled(value)) {
      if (required) errors[field.key] = messages.required;
      continue;
    }
    const limits = field.constraints;
    if (field.type === "money" || field.type === "number") {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) errors[field.key] = messages.number;
      else if ((limits?.min != null && numeric < limits.min) || (limits?.max != null && numeric > limits.max)) errors[field.key] = messages.range;
      else if (limits?.scale != null && (String(value).split(".")[1]?.length ?? 0) > limits.scale) errors[field.key] = messages.scale;
    } else if (
      typeof value === "string" &&
      ((limits?.minLength != null && value.length < limits.minLength) || (limits?.maxLength != null && value.length > limits.maxLength))
    )
      errors[field.key] = messages.length;
  }
  return errors;
}

/** Scope tickets with a generation so delayed lookup results cannot replace a new selection. */
export class IntakeRequestSequence {
  private sequence = 0;
  start() {
    return ++this.sequence;
  }
  isCurrent(sequence: number) {
    return sequence === this.sequence;
  }
  invalidate() {
    this.sequence++;
  }
}
